import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAtom } from "jotai";
import type { JSX } from "react";

import { CpsTextArea } from "corpus";

import { Button } from "@/components/ui/button/button";
import { ProgressRoundedLayout } from "@/components/ui/layout/progress-rounded-layout";
import {
  CardSelectItem,
  CardSelectItemContainer,
  CardSelectRoot,
} from "@/components/ui/select-input/card-select";
import { consultingAtom } from "@/lib/atoms/consulting-atom";

const OUTRO_OPTION = "Outro";
const PROBLEMA_MAX_LENGTH = 300;

const PROBLEMA_OPTIONS = [
  "Não sei por onde começar para atrair pacientes.",
  "Tenho dificuldade em me apresentar e mostrar meu diferencial.",
  "Sinto insegurança sobre como gerenciar as finanças do consultório.",
  "Falta tempo e organização para conciliar tudo.",
  OUTRO_OPTION,
];

const ConsultingProblemaPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [form, setForm] = useAtom(consultingAtom);

  const isOutro = form.problema === OUTRO_OPTION;
  const canContinue =
    form.problema !== "" && (!isOutro || form.problemaOutro.trim() !== "");

  const handleSelect = (value: string): void => {
    setForm((prev) => ({ ...prev, problema: value }));
  };

  const handleOutroChange = (value: string): void => {
    setForm((prev) => ({ ...prev, problemaOutro: value }));
  };

  const goToNextStep = (): void => {
    void navigate({ to: "/onboarding/consulting/acquisition" });
  };

  return (
    <ProgressRoundedLayout progress={0.64}>
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex flex-col gap-4">
          <h1 className="text-xl font-medium leading-tight text-neutral-700">
            Se você pudesse resolver UM problema na sua carreira hoje, qual seria?
          </h1>

          <CardSelectRoot
            name="consulting-problema"
            mode="single"
            defaultValue={form.problema || undefined}
            onChange={handleSelect}
          >
            <CardSelectItemContainer className="gap-3">
              {PROBLEMA_OPTIONS.map((option) => (
                <CardSelectItem key={option} value={option}>
                  {option}
                </CardSelectItem>
              ))}
            </CardSelectItemContainer>
          </CardSelectRoot>

          {isOutro ? (
            <div className="flex flex-col gap-1">
              <CpsTextArea
                value={form.problemaOutro}
                onChange={(event) => handleOutroChange(event.target.value)}
                placeholder="Digite aqui qual seria o problema"
                maxLength={PROBLEMA_MAX_LENGTH}
                rows={3}
              />
              <span className="text-xs text-neutral-500">
                Máx {PROBLEMA_MAX_LENGTH} caracteres
              </span>
            </div>
          ) : null}
        </div>

        <div className="sticky bottom-0 flex w-full flex-col bg-white pb-2 pt-3">
          <Button fullWidth onClick={goToNextStep} disabled={!canContinue}>
            Continuar
          </Button>
        </div>
      </div>
    </ProgressRoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/consulting/career-problem")({
  component: ConsultingProblemaPage,
});
